import { GridApi } from '@ag-grid-community/core';
import { inject, Injectable } from '@angular/core';

import { MessagesConstants } from '@shared/constants/messages.constants';
import { OverlayService } from '@shared/services/overlay.service';
import {
  validateRangeField,
  validateRequiredField,
  validateUniqueField,
} from '@shared/utils/ag-validations/ag-validations';

import { ProtocolEntryGridRow } from '../types/protocol-entry-grid-row';

export type ValidationField = keyof Pick<
  ProtocolEntryGridRow,
  'discontinuation_rate' | 'patient_protocol_name' | 'patient_protocol_sub_type'
>;

export const FIELDS_TO_VALIDATE: ValidationField[] = [
  'discontinuation_rate',
  'patient_protocol_name',
  'patient_protocol_sub_type',
];

type ValidationRules = Record<
  ValidationField,
  {
    range?: {
      errorKey: string;
      message: string;
      value: {
        max: number;
        min: number;
      };
    };
    required?: {
      errorKey: string;
      message: string;
    };
    unique?: {
      errorKey: string;
      message: string;
    };
  }
>;

@Injectable()
export class ProtocolValidationService {
  overlayService = inject(OverlayService);

  private getValidationRules(): ValidationRules {
    return {
      discontinuation_rate: {
        range: {
          errorKey: 'incorrectDiscontinuationRateError',
          message: MessagesConstants.PATIENT_PROTOCOL.INCORRECT_DISCONTINUATION_RATE,
          value: { max: 100, min: 0 },
        },
      },
      patient_protocol_name: {
        required: {
          errorKey: 'missingNameError',
          message: 'Name is required',
        },
        unique: {
          errorKey: 'duplicateNameError',
          message: MessagesConstants.PATIENT_PROTOCOL.DUPLICATE_NAME,
        },
      },
      patient_protocol_sub_type: {
        required: {
          errorKey: 'missingTypeError',
          message: 'Type is required',
        },
      },
    };
  }

  validateField(
    field: ValidationField,
    data: ProtocolEntryGridRow[],
    gridApi: GridApi<ProtocolEntryGridRow>,
    showError = true
  ): boolean {
    const rules = this.getValidationRules()[field];

    if (!rules) {
      return true;
    }

    let valid = true;

    if (rules.required) {
      const isValid = validateRequiredField<ProtocolEntryGridRow>(
        data,
        field,
        rules.required.errorKey,
        gridApi
      );

      if (!isValid && showError) {
        this.overlayService.error(`${rules.required.message}.`);

        return false;
      }

      valid = valid && isValid;
    }

    if (rules.unique) {
      const isValid = validateUniqueField<ProtocolEntryGridRow>(
        data,
        field,
        rules.unique.errorKey,
        gridApi
      );

      if (!isValid && showError) {
        this.overlayService.error(`${rules.unique.message}.`);

        return false;
      }

      valid = valid && isValid;
    }

    if (rules.range) {
      const isValid = validateRangeField<ProtocolEntryGridRow>(
        data.filter((row) => !row.optional),
        field,
        rules.range.errorKey,
        rules.range.value,
        gridApi
      );

      if (!isValid && showError) {
        this.overlayService.error(`${rules.range.message}.`);

        return false;
      }

      valid = valid && isValid;
    }

    return valid;
  }

  validateGridData(data: ProtocolEntryGridRow[], gridApi: GridApi<ProtocolEntryGridRow>): boolean {
    const validationResults = FIELDS_TO_VALIDATE.map((field) => ({
      field,
      isValid: this.validateField(field, data, gridApi, false),
    }));

    const invalidFields = validationResults.filter((result) => !result.isValid);

    if (invalidFields.length > 0) {
      if (invalidFields.length > 1) {
        const errorMessages = invalidFields
          .map((result) => this.getValidationRules()[result.field]?.required?.message)
          .filter(Boolean)
          .join(', ');

        this.overlayService.error(`${errorMessages}.`);
      } else {
        const field = invalidFields[0].field;

        this.validateField(field, data, gridApi);
      }

      return false;
    }

    return true;
  }
}
